// card-grid-carousels.js
// Flicker-free dots, with a resilient scroll lock that always clears.

export default function CardGridCarousels() {
  const containers = document.querySelectorAll('.card-carousel-grid');
  if (!containers || !containers.length) return;
  containers.forEach((container) => setupCarousel(container));
}

function setupCarousel(container) {
  const scroller = container.querySelector('.grid--carousel');
  if (!scroller) return;

  const cards = Array.from(scroller.querySelectorAll('.card-block'));
  if (!cards.length) return;

  const { dots, backBtn, fwdBtn } = createControls(cards.length, container);

  // --- helpers --------------------------------------------------------------

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  // Position of a card’s left edge in the scroller’s scroll coordinate space
  const cardLeft = (el) => {
    const elRect = el.getBoundingClientRect();
    const sRect = scroller.getBoundingClientRect();
    return (elRect.left - sRect.left) + scroller.scrollLeft;
  };

  const indexFromScroll = () => {
    const left = scroller.scrollLeft;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < cards.length; i++) {
      const dist = Math.abs(cardLeft(cards[i]) - left);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  };

  const setActiveDot = (i) => {
    dots.forEach((d, j) => {
      if (j === i) {
        d.classList.add('is-active');
        d.setAttribute('aria-current', 'true');
      } else {
        d.classList.remove('is-active');
        d.removeAttribute('aria-current');
      }
    });
  };

  // --- programmatic scroll lock (resilient) --------------------------------
  let scrollLock = false;
  let targetLeft = null;
  let targetIndex = null;

  // Tolerances: scale with device pixel ratio so “close enough” is realistic
  const EPS = Math.max(1, (window.devicePixelRatio || 1));   // px to consider “at target”
  const IDLE_MS = 80;                                        // idle settle window

  let idleTimer = null;
  let rafId = null;

  const endLock = (useNearest) => {
    scrollLock = false;
    if (useNearest) {
      setActiveDot(indexFromScroll());
    } else if (targetIndex != null) {
      setActiveDot(targetIndex);
    } else {
      setActiveDot(indexFromScroll());
    }
    targetLeft = null;
    targetIndex = null;
  };

  const beginLockedScroll = (index) => {
    scrollLock = true;
    targetIndex = clamp(index, 0, cards.length - 1);
    targetLeft = cardLeft(cards[targetIndex]);
    scroller.scrollTo({ left: targetLeft, behavior: 'smooth' });
  };

  const maybeFinishLockFromScroll = () => {
    if (!scrollLock || targetLeft == null) return false;
    const atTarget = Math.abs(scroller.scrollLeft - targetLeft) <= EPS;
    if (atTarget) {
      endLock(false);
      return true;
    }
    return false;
  };

  const scheduleIdleCheck = () => {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => {
      if (scrollLock) {
        // if we’ve gone idle but didn’t land exactly on target, finish to nearest
        endLock(Math.abs(scroller.scrollLeft - (targetLeft ?? 0)) > EPS);
      } else {
        setActiveDot(indexFromScroll());
      }
    }, IDLE_MS);
  };

  const cancelLockOnUserInterruption = () => {
    if (scrollLock) endLock(true); // snap to nearest if user takes over
  };

  // --- events ---------------------------------------------------------------

  // Dots
  dots.forEach((btn, i) => btn.addEventListener('click', () => beginLockedScroll(i)));

  // Pagers
  backBtn.addEventListener('click', () => {
    const i = indexFromScroll();
    beginLockedScroll(i - 1);
  });
  fwdBtn.addEventListener('click', () => {
    const i = indexFromScroll();
    beginLockedScroll(i + 1);
  });

  // Keyboard
  container.setAttribute('tabindex', '0');
  container.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); beginLockedScroll(indexFromScroll() - 1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); beginLockedScroll(indexFromScroll() + 1); }
    else cancelLockOnUserInterruption();
  });

  // Interruptions: pointer/touch/wheel should cancel lock immediately
  ['pointerdown', 'touchstart', 'wheel'].forEach((evt) =>
    scroller.addEventListener(evt, cancelLockOnUserInterruption, { passive: true })
  );

  // Scroll syncing
  const onScroll = () => {
    if (rafId == null) {
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (scrollLock) {
          // If we’ve effectively reached the target during the animation, finish now.
          if (!maybeFinishLockFromScroll()) {
            // Otherwise, keep dots as-is during the animation (no flicker).
          }
        } else {
          setActiveDot(indexFromScroll());
        }
      });
    }
    scheduleIdleCheck();
  };

  scroller.addEventListener('scroll', onScroll, { passive: true });

  // High-confidence settle signal
  scroller.addEventListener?.('scrollend', () => {
    clearTimeout(idleTimer);
    if (scrollLock) {
      // if we didn’t already finish in onScroll, finish now
      if (!maybeFinishLockFromScroll()) endLock(true);
    } else {
      setActiveDot(indexFromScroll());
    }
  });

  // Layout changes can shift positions; keep alignment stable
  const onResize = () => {
    if (scrollLock && targetIndex != null) {
      // Recalculate target in the new layout and jump there (no animation)
      targetLeft = cardLeft(cards[targetIndex]);
      scroller.scrollTo({ left: targetLeft, behavior: 'auto' });
      // We’re effectively “at target” post-resize.
      endLock(false);
    } else {
      const i = indexFromScroll();
      scroller.scrollTo({ left: cardLeft(cards[i]), behavior: 'auto' });
      setActiveDot(i);
    }
  };
  window.addEventListener('resize', onResize);

  // --- init -----------------------------------------------------------------
  scroller.scrollTo({ left: cardLeft(cards[0]), behavior: 'auto' });
  setActiveDot(0);
}

// ----------------------------------------------------------------------------
// Controls factory (buttons + dots as accessible buttons)
// ----------------------------------------------------------------------------

function createControls(length, parent) {
  const controlsEl = document.createElement('div');
  controlsEl.classList.add('card-carousel-grid__controls');

  // pagers
  const pagersParent = document.createElement('div');
  pagersParent.classList.add('card-carousel-grid__pagers');

  const pagerSVGHTML = '<span><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 12 23"><path fill="#0B262C" d="M4.702.577H.372l6.46 10.573L.056 22.227h4.299l6.863-11.087L4.702.577Z"/></svg></span>';

  const back = document.createElement('button');
  back.classList.add('card-carousel-grid__back-pager');
  back.type = 'button';
  back.innerHTML = pagerSVGHTML;
  back.setAttribute('aria-label', 'Previous card');

  const fwd = document.createElement('button');
  fwd.classList.add('card-carousel-grid__forward-pager');
  fwd.type = 'button';
  fwd.innerHTML = pagerSVGHTML;
  fwd.setAttribute('aria-label', 'Next card');

  pagersParent.appendChild(back);
  pagersParent.appendChild(fwd);
  controlsEl.appendChild(pagersParent);

  // dots
  const list = document.createElement('ul');
  list.classList.add('card-carousel-grid__dots');
  list.setAttribute('role', 'tablist');

  const dots = [];
  for (let i = 0; i < length; i++) {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.classList.add('dot');
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-label', `Go to card ${i + 1}`);
    li.appendChild(btn);
    list.appendChild(li);
    dots.push(btn);
  }

  controlsEl.appendChild(list);
  parent.appendChild(controlsEl);

  return { dots, backBtn: back, fwdBtn: fwd };
}